import { useEffect, useRef } from 'react';
import { userProfileService } from '@/services/user-profile.service';
import { syncCachedUser } from '@/utils/user-cache';

/**
 * Refreshes the cached user once per session load.
 *
 * The user object is cached at login, but `profilePhoto` is a *pre-signed* S3
 * URL with a finite lifetime, and a session can easily outlive it — after which
 * the header avatar silently falls back to initials. Anyone who signed in
 * before a photo was uploaded would likewise never see it without signing out.
 *
 * Fetching the profile once on load re-signs the URL and picks up any change
 * made elsewhere. It also saves the browser's time zone when it has changed.
 * Failures are ignored on purpose: this is a refresh of data
 * the app already has, so a blip should never surface an error or block render.
 */
export function useRefreshCachedProfile(isAuthenticated: boolean): void {
  const refreshedRef = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || refreshedRef.current) return;
    refreshedRef.current = true;

    let cancelled = false;
    userProfileService
      .getProfile()
      .then((user) => {
        if (!cancelled) syncCachedUser(user);
        // Issue-notice emails print times in the admin's zone (NC-4); keep it
        // current with the browser so it's right without a profile visit.
        const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (zone && user.timezone !== zone) {
          void userProfileService.updateProfile({ timezone: zone }).catch(() => undefined);
        }
      })
      .catch(() => {
        // Non-critical — the cached copy stays in use.
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);
}
